import React, { useEffect } from 'react';
import useAppStore from '../../store/useAppStore.js';
import Button from '../common/Button.js';
import ConfirmDialog from '../common/ConfirmDialog.js';
import BranchDialog from '../common/dialogs/BranchDialog.js';
import BranchList from './BranchList.js';
import BranchDetails from './BranchDetails.js';

const AdminBranches = () => {
  const {
    branches,
    selectedBranch,
    loading,
    error,
    dialogLoading,
    loadBranches,
    createBranch,
    updateBranch,
    toggleBranchStatus,
    setSelectedBranch,
    setError
  } = useAppStore((state) => ({
    branches: state.admin.branches,
    selectedBranch: state.admin.selectedBranch,
    loading: state.admin.loading,
    error: state.admin.error,
    dialogLoading: state.admin.dialogLoading,
    loadBranches: state.admin.loadBranches,
    createBranch: state.admin.createBranch,
    updateBranch: state.admin.updateBranch,
    toggleBranchStatus: state.admin.toggleBranchStatus,
    setSelectedBranch: state.admin.setSelectedBranch,
    setError: state.admin.setError
  }));

  const [showBranchDialog, setShowBranchDialog] = React.useState(false);
  const [confirmDialog, setConfirmDialog] = React.useState({ isOpen: false, branch: null });

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  const handleCreateBranch = () => {
    setSelectedBranch(null);
    setShowBranchDialog(true);
  };

  const handleEditBranch = (branch) => {
    setSelectedBranch(branch);
    setShowBranchDialog(true);
  };

  const handleSaveBranch = async (branchData) => {
    try {
      if (selectedBranch) {
        await updateBranch(selectedBranch.id, branchData);
      } else {
        await createBranch(branchData);
      }
      setShowBranchDialog(false);
      setSelectedBranch(null);
    } catch (err) {
      setError(err.message || 'Unable to save branch');
    }
  };

  const handleViewDetails = (branch) => {
    setSelectedBranch(branch);
  };

  const openConfirmDeactivate = (branch) => {
    setConfirmDialog({ isOpen: true, branch });
  };

  const handleConfirmDeactivate = async () => {
    const branch = confirmDialog.branch;
    if (!branch) return;

    try {
      await toggleBranchStatus(branch);
    } catch (err) {
      setError(err.message || 'Unable to update branch status');
    }
    setConfirmDialog({ isOpen: false, branch: null });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Branch Management</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            Create and manage branches across your network. Each branch can have multiple staff members.
          </p>
        </div>
      </div>

      <div>
        <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Branches</h2>
              <p className="mt-1 text-sm text-gray-500">Manage branch details and activation status.</p>
            </div>
            <Button onClick={handleCreateBranch}>
              + New Branch
            </Button>
          </div>

          <BranchList
            branches={branches}
            loading={loading}
            error={error}
            onCreateBranch={handleCreateBranch}
            onEditBranch={handleEditBranch}
            onViewDetails={handleViewDetails}
            onToggleStatus={openConfirmDeactivate}
          />

          {error && (
            <div className="mt-4 rounded-2xl bg-red-50 p-4 text-sm text-red-700">{error}</div>
          )}
        </section>

        {selectedBranch && (
          <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm mt-4">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Branch Details</h2>
              <p className="mt-1 text-sm text-gray-500">View branch information and manager details.</p>
            </div>

            <BranchDetails branch={selectedBranch} />
          </section>
        )}
      </div>

      <BranchDialog
        isOpen={showBranchDialog}
        onClose={() => setShowBranchDialog(false)}
        onSave={handleSaveBranch}
        branch={selectedBranch}
        loading={dialogLoading}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, branch: null })}
        onConfirm={handleConfirmDeactivate}
        title="Confirm action"
        message={`Do you want to ${confirmDialog.branch?.state === 'Active' ? 'deactivate' : 'activate'} this branch?`}
      />
    </div>
  );
};

export default AdminBranches;
